<template>
    <Header />
    <main class="main">
        <div class="page-heading">
            <div>
                <h1>Messages du lobby</h1>
                <p>Gérez les messages de vos écrans. Seuls les messages actifs sont diffusés.</p>
            </div>
            <button class="primary-button" :disabled="pending" @click="openAdd">Ajouter un message</button>
        </div>
        <p v-if="notice" class="success" role="status">{{ notice }}</p>
        <p v-if="loading" role="status">Chargement des messages…</p>
        <div v-else-if="loadError" class="error" role="alert">
            <p>{{ loadError }}</p><button class="secondary-button" @click="loadMessages">Réessayer</button>
        </div>
        <template v-else>
            <div v-if="!messages.length" class="empty">
                <h2>Aucun message pour le moment</h2>
                <p>Ajoutez votre premier message avec le bouton ci-dessus.</p>
            </div>
            <div v-else class="table-wrapper">
                <table>
                    <caption>{{ messages.length }} message{{ messages.length > 1 ? 's' : '' }}</caption>
                    <thead>
                        <tr>
                            <th scope="col">Titre</th>
                            <th scope="col">Message</th>
                            <th scope="col">Statut</th>
                            <th scope="col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in messages" :key="item.id">
                            <template v-if="editingId === item.id">
                                <td><input v-model="editTitle" :aria-label="`Titre de ${item.title}`" maxlength="100"
                                        :disabled="pending" @keydown.enter.prevent="saveEdit"></td>
                                <td><textarea v-model="editMessage" :aria-label="`Contenu de ${item.title}`" rows="6"
                                        :disabled="pending"></textarea>
                                    <p v-if="editError" class="error" role="alert">{{ editError }}</p>
                                </td>
                                <td><select v-model="editStatus" :aria-label="`Statut de ${item.title}`"
                                        :disabled="pending">
                                        <option v-for="(label, value) in statuses" :key="value" :value="value">{{ label
                                            }}</option>
                                    </select></td>
                                <td>
                                    <div class="actions"><button class="success-button" :disabled="pending"
                                            @click="saveEdit">{{ pending ? 'Enregistrement…' : 'Enregistrer'
                                            }}</button><button class="secondary-button-outlined" :disabled="pending"
                                            @click="editingId = null">Annuler</button></div>
                                </td>
                            </template>
                            <template v-else>
                                <th scope="row">{{ item.title }}</th>
                                <td>
                                    <div class="message-text">{{ item.message }}</div>
                                </td>
                                <td><span class="status-badge" :class="item.status">{{ statuses[item.status] ||
                                        item.status }}</span></td>
                                <td>
                                    <div class="actions"><button class="primary-button-outlined"
                                            :disabled="pending || editingId !== null"
                                            :aria-label="`Modifier ${item.title}`"
                                            @click="startEdit(item)">Modifier</button><button
                                            class="danger-button-outlined" :disabled="pending || editingId !== null"
                                            :aria-label="`Supprimer ${item.title}`"
                                            @click="openDelete(item)">Supprimer</button></div>
                                </td>
                            </template>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </main>
    <Modal v-model="addModal">
        <form class="message-form" aria-labelledby="add-title" @submit.prevent="addMessage">
            <h2 id="add-title">Ajouter un message</h2>
            <p class="intro">Rédigez le contenu à afficher sur les écrans du lobby.</p>
            <label for="message-title">Titre</label><input id="message-title" v-model="title" maxlength="100" required
                :disabled="pending" placeholder="Ex. : Bienvenue">
            <label for="message-content">Message</label><textarea id="message-content" v-model="message" rows="6"
                required :disabled="pending" placeholder="Votre message…"></textarea>
            <label for="message-status">Statut</label><select id="message-status" v-model="status" :disabled="pending"
                aria-describedby="status-help">
                <option v-for="(label, value) in statuses" :key="value" :value="value">{{ label }}</option>
            </select>
            <p id="status-help" class="hint">Un message actif est diffusé dans le lobby. Un brouillon ou un message
                archivé
                n’est pas affiché.</p>
            <p v-if="addError" class="error" role="alert">{{ addError }}</p>
            <div class="form-actions"><button type="button" class="secondary-button-outlined" :disabled="pending"
                    @click="addOpen = false">Annuler</button><button class="primary-button" :disabled="pending">{{
                        pending ?
                    'Ajout…' : 'Ajouter le message' }}</button></div>
        </form>
    </Modal>
    <Modal v-model="deleteModal">
        <div class="message-form" aria-labelledby="delete-title">
            <h2 id="delete-title">Supprimer ce message ?</h2>
            <p><strong>{{ deleteTarget?.title }}</strong> et son contenu seront supprimés définitivement et ne seront
                plus
                diffusés dans le lobby.</p>
            <p v-if="deleteError" class="error" role="alert">{{ deleteError }}</p>
            <div class="form-actions"><button class="secondary-button-outlined" :disabled="pending"
                    @click="deleteTarget = null">Annuler</button><button class="danger-button" :disabled="pending"
                    @click="deleteMessage">{{ pending ? 'Suppression…' : 'Supprimer le message' }}</button></div>
        </div>
    </Modal>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import apiClient from '@/api'
import Header from '@/components/Header.vue'
import Modal from '@/components/Modal.vue'

const messages = ref([])
const loading = ref(true)
const loadError = ref('')
const notice = ref('')
const editingId = ref(null)
const editTitle = ref('')
const editMessage = ref('')
const editStatus = ref('active')
const editError = ref('')
const addOpen = ref(false)
const title = ref('')
const message = ref('')
const status = ref('active')
const addError = ref('')
const deleteTarget = ref(null)
const deleteError = ref('')
const pending = ref(false)
const statuses = { active: 'Actif', draft: 'Brouillon', archived: 'Archivé' }
const addModal = computed({ get: () => addOpen.value, set: value => { if (!pending.value) addOpen.value = value } })
const deleteModal = computed({ get: () => !!deleteTarget.value, set: value => { if (!value && !pending.value) deleteTarget.value = null } })
const errorMessage = error => {
    const data = error.response?.data
    if (typeof data?.message === 'string') return data.message
    if (typeof data?.detail === 'string') return data.detail
    if (data && typeof data === 'object') return Object.values(data).flat().join(' ')
    return 'La demande a échoué. Veuillez réessayer.'
}
const loadMessages = async () => {
    loading.value = true
    loadError.value = ''
    try {
        const { data } = await apiClient.get('lobby_messages/')
        messages.value = data
    } catch (error) { loadError.value = errorMessage(error) }
    finally { loading.value = false }
}
const openAdd = () => {
    title.value = ''
    message.value = ''
    status.value = 'active'
    addError.value = ''
    notice.value = ''
    addOpen.value = true
}
const startEdit = item => {
    editingId.value = item.id
    editTitle.value = item.title
    editMessage.value = item.message
    editStatus.value = item.status
    editError.value = ''
    notice.value = ''
}
const saveEdit = async () => {
    if (pending.value) return
    editError.value = ''
    if (!editTitle.value.trim() || !editMessage.value.trim()) {
        editError.value = 'Saisissez un titre et le contenu du message.'
        return
    }
    pending.value = true
    try {
        const { data } = await apiClient.patch(`lobby_messages/${editingId.value}/`, {
            title: editTitle.value.trim(), message: editMessage.value.trim(), status: editStatus.value,
        })
        messages.value = messages.value.map(item => item.id === data.id ? data : item)
        editingId.value = null
        notice.value = 'Le message a été mis à jour.'
    } catch (error) { editError.value = errorMessage(error) }
    finally { pending.value = false }
}
const addMessage = async () => {
    if (pending.value) return
    addError.value = ''
    if (!title.value.trim() || !message.value.trim()) {
        addError.value = 'Saisissez un titre et le contenu du message.'
        return
    }
    pending.value = true
    try {
        const { data } = await apiClient.post('add_message/', {
            title: title.value.trim(), message: message.value.trim(), status: status.value,
        })
        messages.value.unshift(data.data)
        addOpen.value = false
        notice.value = 'Le message a été ajouté.'
    } catch (error) { addError.value = errorMessage(error) }
    finally { pending.value = false }
}
const openDelete = item => {
    deleteTarget.value = item
    deleteError.value = ''
    notice.value = ''
}
const deleteMessage = async () => {
    if (pending.value) return
    pending.value = true
    deleteError.value = ''
    const id = deleteTarget.value.id
    try {
        await apiClient.delete(`lobby_messages/${id}/`)
        messages.value = messages.value.filter(item => item.id !== id)
        if (editingId.value === id) editingId.value = null
        deleteTarget.value = null
        notice.value = 'Le message a été supprimé.'
    } catch (error) { deleteError.value = errorMessage(error) }
    finally { pending.value = false }
}
onMounted(loadMessages)
</script>

<style scoped lang="scss">
.main {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 2rem;
    background: white;
    border-radius: 15px;
}

.page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;

    h1 {
        color: $dark-blue;
        font-size: 1.7rem;
        margin: 0 0 .5rem;
    }

    p {
        margin: 0;
        color: #646d7b;
    }
}

.table-wrapper {
    overflow-x: auto;
}

table {
    width: 100%;
    table-layout: fixed;
    border: none;
    border-collapse: separate;
    border-spacing: 0;

    caption {
        text-align: left;
        color: #646d7b;
        font-size: .85rem;
        margin-bottom: .75rem;
    }
}

th,
td {
    border: none;
    text-align: left;
    overflow-wrap: anywhere;
}

thead th {
    background: #f4f7fb;
    color: $dark-blue;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    padding: .8rem 1rem;
    border-bottom: 2px solid #e3e9f2;

    &:first-child {
        width: 20%;
        border-top-left-radius: 10px;
    }

    &:nth-child(3) {
        width: 14%;
    }

    &:last-child {
        width: 26%;
        border-top-right-radius: 10px;
    }
}

tbody td,
tbody th {
    background: white;
    padding: .85rem 1rem;
    border-bottom: 1px solid #eef1f6;
    vertical-align: middle;
    transition: background-color .15s;
}

tbody th {
    font-weight: 600;
    color: $dark-blue;
}

tbody tr:last-child> :is(th, td) {
    border-bottom: none;
}

tbody tr:hover> :is(th, td) {
    background: #f8fafd;
}

@media (prefers-reduced-motion: reduce) {

    tbody td,
    tbody th {
        transition: none;
    }
}

a {
    color: $dark-blue;
    font-size: .9rem;
}

.actions,
.form-actions {
    display: flex;
    gap: .5rem;
    flex-wrap: wrap;
}

.actions button {
    font-size: .85rem;
    padding: .5rem .75rem;
}

input,
textarea,
select {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    font-family: inherit;
    font-size: .95rem;
    padding: .6rem .9rem;
    border: 1px solid #dbdee3;
    border-radius: 8px;
    outline-color: $dark-blue;
}

.message-form {
    width: min(460px, calc(100vw - 8rem));
    padding-block: 1rem;

    h2 {
        color: $dark-blue;
        margin-top: 0;
        padding-right: 1rem;
    }

    p {
        line-height: 1.6;
    }

    label {
        display: block;
        font-weight: 600;
        margin: 1.25rem 0 .5rem;
    }
}

.intro {
    color: #646d7b;
}

.form-actions {
    justify-content: flex-end;
    margin-top: 1.75rem;
}

.hint {
    color: #646d7b;
    font-size: .8rem;
    line-height: 1.5;
    margin-bottom: 0;
}

.error {
    color: #a62323;
    font-size: .9rem;
}

.success {
    color: #246747;
    padding: .75rem 1rem;
    background: #e7f6ee;
    border-radius: 8px;
}

.empty {
    padding: 2.5rem 1rem;
    text-align: center;
    border: 1px dashed #cdd4df;
    border-radius: 10px;

    h2 {
        font-size: 1.2rem;
        color: $dark-blue;
    }
}

button:disabled {
    opacity: .55;
    cursor: not-allowed;
}

:is(button, a):focus-visible {
    outline: 3px solid $dark-blue;
    outline-offset: 3px;
}

@media (max-width: 760px) {
    .main {
        margin: 1rem;
        padding: 1rem;
    }

    table {
        min-width: 850px;
    }

    .form-actions {
        flex-direction: column-reverse;
    }
}

textarea {
    resize: vertical;
}

.message-text {
    white-space: pre-wrap;
    line-height: 1.6;
    max-height: 12rem;
    overflow-y: auto;
}

.status-badge {
    display: inline-block;
    padding: .25rem .6rem;
    border-radius: 6px;
    background: #eef1f6;
    color: #586174;
    font-size: .8rem;
}

.status-badge.active {
    background: #e7f6ee;
    color: #246747;
}

.status-badge.archived {
    background: #fff0e8;
    color: #96522b;
}
</style>
